import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Camp, Sectiune, BaraSalvare } from '@/ui/form'
import { PartenerPicker } from '@/ui/partner-picker'
import { MONEDE, TIPURI_SERVICIU, azi } from '@/lib/domeniu'

export function OfertaNoua() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [o, setO] = useState({
    contact_name: '', contact_email: '', contact_phone: '',
    destination: '', hotel: '', service_type: 'SEJUR_CHARTER',
    begin_date: '', end_date: '', adults: '2', children: '0',
    total_value: '', cost_value: '', currency: 'EUR',
    valid_until: '', notes: '',
  })
  const [partnerId, setPartnerId] = useState<number | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const s = (k: keyof typeof o) => (v: string) => setO((x) => ({ ...x, [k]: v }))
  const nr = (v: string) => Number(String(v).replace(',', '.')) || 0

  const salveaza = useMutation({
    mutationFn: () => {
      if (!o.contact_name.trim()) throw new ApiError(400, 'Clientul e obligatoriu')
      return api.post<{ id: number }>('/offers/', {
        partner_id: partnerId ?? undefined,
        contact_name: o.contact_name,
        contact_email: o.contact_email || undefined,
        contact_phone: o.contact_phone || undefined,
        destination: o.destination || undefined,
        hotel: o.hotel || undefined,
        service_type: o.service_type,
        begin_date: o.begin_date || undefined,
        end_date: o.end_date || undefined,
        adults: Number(o.adults) || 0,
        children: Number(o.children) || 0,
        total_value: nr(o.total_value),
        cost_value: nr(o.cost_value),
        currency: o.currency,
        valid_until: o.valid_until || undefined,
        notes: o.notes || undefined,
      })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['oferte'] }); nav('/rezervari/oferte') },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  const marja = nr(o.total_value) - nr(o.cost_value)

  return (
    <>
      <PageHeader title="Oferta noua" />
      <div className="px-8 pb-0">
        <Sectiune titlu="Client" descriere="Cui ii facem propunerea.">
          <PartenerPicker valoare={o.contact_name} seteaza={s('contact_name')} obligatoriu
            alege={(p) => {
              setPartnerId(p.id)
              setO((x) => ({ ...x, contact_email: p.email ?? x.contact_email, contact_phone: p.phone ?? x.contact_phone }))
            }} />
          <Camp eticheta="Email" tip="email" valoare={o.contact_email} seteaza={s('contact_email')} />
          <Camp eticheta="Telefon" tip="tel" valoare={o.contact_phone} seteaza={s('contact_phone')} />
        </Sectiune>

        <Sectiune titlu="Calatoria" descriere="Ce ii propunem.">
          <Camp eticheta="Tip serviciu" tip="select" valoare={o.service_type} seteaza={s('service_type')}
            optiuni={TIPURI_SERVICIU} />
          <Camp eticheta="Destinatie" valoare={o.destination} seteaza={s('destination')} />
          <Camp eticheta="Hotel" valoare={o.hotel} seteaza={s('hotel')} latime="plin" />
          <Camp eticheta="Plecare" tip="date" valoare={o.begin_date} seteaza={s('begin_date')} />
          <Camp eticheta="Intoarcere" tip="date" valoare={o.end_date} seteaza={s('end_date')} />
          <Camp eticheta="Adulti" tip="number" valoare={o.adults} seteaza={s('adults')} />
          <Camp eticheta="Copii" tip="number" valoare={o.children} seteaza={s('children')} />
        </Sectiune>

        <Sectiune titlu="Bani" descriere={`Marja: ${marja.toFixed(2)} ${o.currency}`}>
          <Camp eticheta="Pret de vanzare" valoare={o.total_value} seteaza={s('total_value')} />
          <Camp eticheta="Cost furnizor" valoare={o.cost_value} seteaza={s('cost_value')} />
          <Camp eticheta="Moneda" tip="select" valoare={o.currency} seteaza={s('currency')} optiuni={MONEDE} />
          <Camp eticheta="Valabila pana la" tip="date" valoare={o.valid_until} seteaza={s('valid_until')}
            indiciu={`azi: ${azi()}`} />
          <Camp eticheta="Note pentru client" tip="textarea" valoare={o.notes} seteaza={s('notes')} latime="plin" />
        </Sectiune>

        <BaraSalvare salveaza={() => { setEroare(null); salveaza.mutate() }}
          seSalveaza={salveaza.isPending} eroare={eroare} eticheta="Salveaza oferta" />
      </div>
    </>
  )
}
